import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';

const progressBarSizes = ['xs', 'sm', 'md', 'lg'] as const;
export type ProgressBarSize = (typeof progressBarSizes)[number];

const progressBarVariants = ['primary', 'success', 'caution', 'error', 'info'] as const;
export type ProgressBarVariant = (typeof progressBarVariants)[number];

export interface ProgressBarProps {
  value: number;
  max?: number;
  size?: ProgressBarSize;
  variant?: ProgressBarVariant;
  label: string;
  labelVisuallyHidden?: boolean;
  valueText?: string;
  className?: ClassName;
  labelClassName?: ClassName;
}

const trackHeightBySize: Record<ProgressBarSize, string> = {
  xs: 'h-1',
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-3',
};

const fillBackgroundByVariant: Record<ProgressBarVariant, string> = {
  primary: 'bg-primary-main',
  success: 'bg-success-main',
  caution: 'bg-caution-main',
  error: 'bg-error-main',
  info: 'bg-info-main',
};

export const ProgressBar = ({
  value,
  max = 100,
  size = 'sm',
  variant = 'primary',
  label,
  labelVisuallyHidden = false,
  valueText,
  className,
  labelClassName,
}: ProgressBarProps) => {
  const safeMax = max > 0 ? max : 1;
  const clampedValue = Math.min(Math.max(value, 0), safeMax);
  const percentage = (clampedValue / safeMax) * 100;

  return (
    <div className={twx('flex w-full flex-col gap-1', className)}>
      <div
        className={twx(
          'justify-betweenfont-medium flex flex-col items-baseline text-content-main text-sm',
          { 'sr-only': labelVisuallyHidden },
          labelClassName,
        )}
      >
        <span className={tw`whitespace-nowrap`}>{label}</span>
        {valueText && !labelVisuallyHidden && (
          <span className={tw`whitespace-nowrap text-content-subtle text-xs tabular-nums`}>
            {valueText}
          </span>
        )}
      </div>

      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-valuetext={valueText}
        className={twx(
          'w-full overflow-hidden rounded-full bg-background-disabled',
          trackHeightBySize[size],
        )}
      >
        <div
          style={{ width: `${percentage}%` }}
          className={twx(
            'h-full rounded-full transition-[width] duration-300 ease-out',
            fillBackgroundByVariant[variant],
          )}
        />
      </div>
    </div>
  );
};
